import { PlusCircle, Trash2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useOverlay } from '../../contexts/OverlayContext';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { customerApi } from '../Onboarding/CustomerOnboarding/customerApi';
import { getAllSites } from '../SiteManagement/siteApi';
import Input from '../ui/input.js';
import { invoiceApi } from './invoiceApi';

const getTodayDate = () => new Date().toISOString().split('T')[0];

/** Convert YYYY-MM-DD string to Date, or null if invalid */
const toDateObj = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Format a Date or date string to YYYY-MM-DD for input min/max */
const toYYYYMMDD = (dateOrStr) => {
  const d = typeof dateOrStr === 'string' ? toDateObj(dateOrStr) : dateOrStr;
  return d ? d.toISOString().split('T')[0] : getTodayDate();
};

const createEmptyLineItem = () => ({
  name: '',
  hsnSacCode: '',
  quantity: '',
  units: '',
  rate: '',
  amount: '',
});

const uniqueId = () => Math.random().toString(36).slice(2, 10);

const createTaxRow = () => ({
  id: uniqueId(),
  label: '',
  applyOn: 'subtotalAfterDiscount',
  percentage: '',
});

const numberOrZero = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const resolveSiteValue = (site) => {
  const value =
    site?.site_id ??
    site?.siteId ??
    site?.id ??
    site?._id ??
    site?.site_code ??
    site?.siteCode;
  return value !== undefined && value !== null ? String(value) : '';
};

const resolveSiteLabel = (site) =>
  site?.site_name ||
  site?.name ||
  site?.siteCode ||
  site?.site_code ||
  `Site ${site?.site_id ?? site?.id ?? ''}`;

const PARSED_NOTE_KEYS = [
  'po number', 'po date', 'payment mode', 'insurance',
  'mode of transaction', 'transaction id', 'amount paid',
  'poc name', 'poc phone', 'poc role', 'poc email',
  'poc', 'contact person', 'contact name', 'contact phone', 'contact email',
  'discount', 'enter %',
];

/** Parse notes/terms text to extract field values (e.g. "PO Number: 2", "POC Name: John") */
const parseNotesForFields = (notesText = '') => {
  const text = String(notesText).trim();
  if (!text) return { parsed: {}, remainingNotes: text };
  const result = {};
  const stripRupee = (s) => String(s).replace(/^₹\s*/, '').trim();
  const lines = text.split(/\r?\n/);
  const keyMap = {
    'po number': 'poNumber',
    'po date': 'poDate',
    'payment mode': 'paymentMode',
    'insurance': 'insurance',
    'mode of transaction': 'modeOfTransaction',
    'transaction id': 'transactionId',
    'amount paid': 'amountPaid',
    'poc name': 'pocName',
    'poc phone': 'pocPhone',
    'poc role': 'pocRole',
    'poc email': 'pocEmail',
    'poc': 'pocName',
    'contact person': 'pocName',
    'contact name': 'pocName',
    'contact phone': 'pocPhone',
    'contact email': 'pocEmail',
    'discount': 'discount',
    'enter %': 'discount',
  };
  for (const line of lines) {
    const match = line.match(/^([^:]+):\s*(.*)$/);
    if (!match) continue;
    const key = match[1].trim().toLowerCase();
    const value = match[2].trim();
    const field = keyMap[key];
    if (!field) continue;
    if (result[field] !== undefined && result[field] !== '') continue;
    if (field === 'insurance' || field === 'amountPaid') {
      result[field] = stripRupee(value);
    } else if (field === 'discount') {
      result[field] = value.replace(/%/g, '').trim();
    } else {
      result[field] = value;
    }
  }
  const remainingLines = lines.filter((line) => {
    const m = line.match(/^([^:]+):/);
    return !m || !PARSED_NOTE_KEYS.includes(m[1].trim().toLowerCase());
  });
  const remainingNotes = remainingLines.join('\n').trim();
  return { parsed: result, remainingNotes };
};

/** Validate email format (when value is provided) */
const isValidEmail = (value) => {
  const v = String(value || '').trim();
  if (!v) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
};

/** Validate mobile number: 10 digits, optional +91 or 0 prefix, spaces/dashes allowed */
const isValidPhone = (value) => {
  const v = String(value || '').trim();
  if (!v) return true;
  const digits = v.replace(/\D/g, '');
  if (digits.length === 10) return true;
  if (digits.length === 11 && digits.startsWith('0')) return true;
  if (digits.length === 12 && digits.startsWith('91')) return true;
  return false;
};

const CreateInvoice = () => {
  // Log screen view
  useScreenLogging('CreateInvoice', {
    component: 'CreateInvoice',
  });

  const { openOverlay } = useOverlay();
  const navigate = useNavigate();
  const location = useLocation();
  const appliedPrefillRef = useRef(false);
  const [formData, setFormData] = useState({
    siteSelection: '',
    clientSelection: '',
    date: getTodayDate(),
    poNumber: '',
    poDate: '',
    paymentMode: '',
    paymentTerms: '',
    estimatedShipDate: '',
    dateOfExpiry: '',
    transportationMode: '',
    transportationTerms: '',
    shippingAddress: '',
    shippingPackages: '',
    grossQuantity: '',
    netQuantity: '',
    pocName: '',
    pocPhone: '',
    pocRole: '',
    pocEmail: '',
    specialNotes: '',
    termsOfSale: '',
    billingAddress: '',
    billingContact: '',
    billingEmail: '',
    billingPhone: '',
    paymentReference: '',
    useBillingForShipping: false,
    modeOfTransaction: '',
    transactionId: '',
    amountPaid: '',
    invoiceStatus: 'sent',
  });

  const [lineItems, setLineItems] = useState([createEmptyLineItem()]);
  const [discount, setDiscount] = useState({
    type: 'percentage',
    value: '',
    shippingHandling: '',
    insurance: '',
  });
  const [taxRows, setTaxRows] = useState([createTaxRow()]);
  const [hsnSacRateByCode, setHsnSacRateByCode] = useState({});
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [isMobileView, setIsMobileView] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [isCustomersLoading, setIsCustomersLoading] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitFeedback, setSubmitFeedback] = useState({ success: '', error: '' });
  const [pocErrors, setPocErrors] = useState({ phone: '', email: '' });

  const formatCurrency = (amount = 0) =>
    Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(Number.isFinite(amount) ? amount : 0);

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems((prev) => {
      const next = [...prev];
      const updated = { ...next[index], [field]: value };
      if (field === 'quantity' || field === 'rate') {
        // Prevent negative quantities and rates – treat negatives as 0
        const rawQuantity =
          field === 'quantity' ? numberOrZero(value) : numberOrZero(updated.quantity);
        const rawRate =
          field === 'rate' ? numberOrZero(value) : numberOrZero(updated.rate);
        const quantity = Math.max(0, rawQuantity);
        const rate = Math.max(0, rawRate);
        const amount = quantity * rate;
        updated.amount = amount > 0 ? amount.toFixed(2) : '';
      }
      next[index] = updated;
      return next;
    });
  };

  const handleAddLineItem = () => {
    setLineItems((prev) => [...prev, createEmptyLineItem()]);
  };

  const handleRemoveLineItem = (index) => {
    setLineItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleDiscountChange = (field, value) => {
    setDiscount((prev) => ({ ...prev, [field]: value }));
  };

  const handleTaxRowChange = (id, field, value) => {
    setTaxRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleAddTaxRow = () => {
    setTaxRows((prev) => [...prev, createTaxRow()]);
  };

  const handleRemoveTaxRow = (id) => {
    setTaxRows((prev) => prev.filter((row) => row.id !== id));
  };

  const handleHsnSacRateChange = (code, field, value) => {
    setHsnSacRateByCode((prev) => ({
      ...prev,
      [code]: {
        centralRate:
          field === 'centralRate' ? value : (prev[code]?.centralRate ?? ''),
        stateUtRate: field === 'stateUtRate' ? value : (prev[code]?.stateUtRate ?? ''),
        igstRate: field === 'igstRate' ? value : (prev[code]?.igstRate ?? ''),
      },
    }));
  };

  const summary = useMemo(() => {
    const subtotal = lineItems.reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0),
      0
    );
    const discountValue = numberOrZero(discount.value);
    const computedDiscount =
      discount.type === 'percentage'
        ? subtotal * (discountValue / 100)
        : discountValue;
    const shippingHandling = numberOrZero(discount.shippingHandling);
    const insurance = numberOrZero(discount.insurance);
    const subtotalAfterDiscount = Math.max(subtotal - computedDiscount, 0);
    const netTotalBeforeTax = subtotalAfterDiscount + shippingHandling + insurance;
    // Match backend/Fdoc: grand_total = subtotal − discount + shipping_total + tax_total (no insurance)
    const grandTotalBase = subtotalAfterDiscount + shippingHandling;

    const resolveBaseAmount = (applyOn) => {
      switch (applyOn) {
        case 'subtotal':
          return subtotal;
        case 'subtotalAfterDiscount':
          return subtotalAfterDiscount;
        case 'shippingHandling':
          return shippingHandling;
        case 'insurance':
          return insurance;
        case 'netTotalBeforeTax':
          return netTotalBeforeTax;
        default:
          return subtotalAfterDiscount;
      }
    };

    const taxes = taxRows.map((row) => {
      const base = resolveBaseAmount(row.applyOn);
      const percent = numberOrZero(row.percentage);
      const amount = base * (percent / 100);
      return { ...row, amount };
    });

    const totalTax = taxes.reduce((sum, row) => sum + row.amount, 0);
    const grandTotal = grandTotalBase + totalTax;
    const totalQuantity = lineItems.reduce(
      (sum, item) => sum + numberOrZero(item.quantity),
      0
    );

    return {
      subtotal,
      computedDiscount,
      shippingHandling,
      insurance,
      subtotalAfterDiscount,
      netTotalBeforeTax,
      taxes,
      totalTax,
      grandTotal,
      totalQuantity,
    };
  }, [discount, lineItems, taxRows]);

  const gstFooterLabel = useMemo(() => {
    const rowsWithPct = taxRows.filter((r) => numberOrZero(r.percentage) > 0);
    if (rowsWithPct.length === 0) return 'GST';
    if (rowsWithPct.length === 1) {
      const pct = rowsWithPct[0].percentage;
      const name = (rowsWithPct[0].label || '').trim();
      return name ? `${name}(${pct}%)` : `GST(${pct}%)`;
    }
    const totalPct = rowsWithPct.reduce((s, r) => s + numberOrZero(r.percentage), 0);
    return `GST(${totalPct}%)`;
  }, [taxRows]);

  const formatTotalQuantity = (qty) =>
    Number.isInteger(qty) ? String(qty) : qty.toFixed(2);

  const formatTaxTableAmount = (amount = 0) =>
    Number(amount || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const hsnSacTaxSummary = useMemo(() => {
    const groups = new Map();
    lineItems.forEach((item) => {
      const code = String(item.hsnSacCode || '').trim() || '—';
      const amount = parseFloat(item.amount) || 0;
      const existing = groups.get(code) || { code, taxableValue: 0 };
      existing.taxableValue += amount;
      groups.set(code, existing);
    });

    const rawSubtotal = lineItems.reduce(
      (sum, item) => sum + (parseFloat(item.amount) || 0),
      0
    );
    const discountFactor =
      rawSubtotal > 0 ? summary.subtotalAfterDiscount / rawSubtotal : 1;

    let defaultCentralRate = 0;
    let defaultStateRate = 0;
    let defaultIgstRate = 0;

    taxRows.forEach((row) => {
      if (row.applyOn !== 'subtotal' && row.applyOn !== 'subtotalAfterDiscount') {
        return;
      }
      const pct = numberOrZero(row.percentage);
      const label = (row.label || '').trim().toUpperCase();
      if (label === 'IGST') {
        defaultIgstRate += pct;
      } else if (label === 'CGST') {
        defaultCentralRate += pct;
      } else if (label === 'SGST') {
        defaultStateRate += pct;
      } else {
        defaultCentralRate += pct / 2;
        defaultStateRate += pct / 2;
      }
    });

    let rows = Array.from(groups.values()).map((group) => {
      const taxableValue = group.taxableValue * discountFactor;
      const stored = hsnSacRateByCode[group.code] || {};
      const centralRate =
        stored.centralRate !== undefined && stored.centralRate !== ''
          ? numberOrZero(stored.centralRate)
          : defaultCentralRate;
      const stateRate =
        stored.stateUtRate !== undefined && stored.stateUtRate !== ''
          ? numberOrZero(stored.stateUtRate)
          : defaultStateRate;
      const igstRate =
        stored.igstRate !== undefined && stored.igstRate !== ''
          ? numberOrZero(stored.igstRate)
          : defaultIgstRate;
      const rowCombinedRate = centralRate + stateRate + igstRate;
      let centralAmount = 0;
      let stateAmount = 0;
      let igstAmount = 0;

      if (rowCombinedRate > 0) {
        centralAmount = taxableValue * (centralRate / 100);
        stateAmount = taxableValue * (stateRate / 100);
        igstAmount = taxableValue * (igstRate / 100);
      }

      return {
        code: group.code,
        taxableValue,
        centralRate,
        centralRateInput: stored.centralRate ?? '',
        stateRate,
        stateUtRateInput: stored.stateUtRate ?? '',
        igstRate,
        igstRateInput: stored.igstRate ?? '',
        centralAmount,
        stateAmount,
        igstAmount,
        totalTax: centralAmount + stateAmount + igstAmount,
      };
    });

    const rowsTaxableSum = rows.reduce((sum, row) => sum + row.taxableValue, 0);
    const defaultTotalRate = defaultCentralRate + defaultStateRate + defaultIgstRate;
    const hasAnyRowRate = rows.some(
      (row) => row.centralRate + row.stateRate + row.igstRate > 0
    );
    if (!hasAnyRowRate && summary.totalTax > 0 && rowsTaxableSum > 0) {
      rows = rows.map((row) => {
        const share = row.taxableValue / rowsTaxableSum;
        const lineTotalTax = summary.totalTax * share;
        if (defaultIgstRate > 0 && defaultCentralRate === 0 && defaultStateRate === 0) {
          return {
            ...row,
            centralAmount: 0,
            stateAmount: 0,
            igstAmount: lineTotalTax,
            totalTax: lineTotalTax,
          };
        }
        if (defaultTotalRate > 0) {
          const centralAmount =
            lineTotalTax * (defaultCentralRate / defaultTotalRate);
          const stateAmount = lineTotalTax * (defaultStateRate / defaultTotalRate);
          const igstAmount = lineTotalTax * (defaultIgstRate / defaultTotalRate);
          return {
            ...row,
            centralAmount,
            stateAmount,
            igstAmount,
            totalTax: lineTotalTax,
          };
        }
        return {
          ...row,
          centralAmount: lineTotalTax / 2,
          stateAmount: lineTotalTax / 2,
          igstAmount: 0,
          totalTax: lineTotalTax,
        };
      });
    }

    rows.sort((a, b) => a.code.localeCompare(b.code));
    rows = rows.filter((row) => row.taxableValue > 0);

    const totals = rows.reduce(
      (acc, row) => ({
        taxableValue: acc.taxableValue + row.taxableValue,
        centralAmount: acc.centralAmount + row.centralAmount,
        stateAmount: acc.stateAmount + row.stateAmount,
        igstAmount: acc.igstAmount + row.igstAmount,
        totalTax: acc.totalTax + row.totalTax,
      }),
      {
        taxableValue: 0,
        centralAmount: 0,
        stateAmount: 0,
        igstAmount: 0,
        totalTax: 0,
      }
    );

    return { rows, totals };
  }, [
    lineItems,
    taxRows,
    summary.subtotalAfterDiscount,
    summary.totalTax,
    hsnSacRateByCode,
  ]);

  useEffect(() => {
    if (!formData.useBillingForShipping) {
      return;
    }
    setFormData((prev) => {
      if (prev.shippingAddress === prev.billingAddress) {
        return prev;
      }
      return {
        ...prev,
        shippingAddress: prev.billingAddress,
      };
    });
  }, [formData.useBillingForShipping, formData.billingAddress]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const updateView = (event) => setIsMobileView(event.matches);
    updateView(mediaQuery);
    mediaQuery.addEventListener('change', updateView);

    return () => mediaQuery.removeEventListener('change', updateView);
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError('');
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        if (isMounted) {
          setSites(sorted);
        }
      } catch (error) {
        if (isMounted) {
          setSitesError(
            error?.message || 'Unable to load sites. Please try again later.'
          );
        }
      } finally {
        if (isMounted) {
          setIsSitesLoading(false);
        }
      }
    };

    fetchSites();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const fetchCustomers = async () => {
      setIsCustomersLoading(true);
      setCustomersError('');
      try {
        const result = await customerApi.listCustomers({ limit: 100, status: 'active' });
        if (result.success) {
          const sorted = [...(result.customers || [])].sort((a, b) =>
            (a?.customer_name || a?.name || '').localeCompare(b?.customer_name || b?.name || '')
          );
          // Log customer data for debugging
          console.log('Invoice: Fetched customers:', sorted.map(c => ({
            customer_id: c.customer_id,
            id: c.id,
            customer_name: c.customer_name || c.name,
            full: c
          })));
          if (isMounted) {
            setCustomers(sorted);
            // Clear selection if the selected customer no longer exists
            if (formData.clientSelection) {
              const stillExists = sorted.some(c => (c.customer_id || c.id) === formData.clientSelection);
              if (!stillExists) {
                console.warn('Selected customer no longer exists, clearing selection');
                handleFormChange('clientSelection', '');
              }
            }
          }
        } else {
          if (isMounted) {
            setCustomersError(result.error || 'Failed to load customers');
          }
        }
      } catch (error) {
        if (isMounted) {
          setCustomersError(error?.message || 'Unable to load customers. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsCustomersLoading(false);
        }
      }
    };

    fetchCustomers();
    return () => {
      isMounted = false;
    };
  }, []);

  // Prefill form when navigated from Fdoc with an invoice (status Sent)
  useEffect(() => {
    const stateInvoice = location.state?.invoice;
    const fromFdoc = location.state?.fromFdoc;
    if (!stateInvoice || appliedPrefillRef.current) return;
    // When opening a single invoice from Fdoc we fetch it via getInvoice; don't wait for sites/customers
    const willFetchFullInvoice = fromFdoc && stateInvoice.id;
    if (!willFetchFullInvoice && (isSitesLoading || isCustomersLoading)) return;

    const applyPrefill = (invoice) => {
      appliedPrefillRef.current = true;
      const siteVal = invoice.siteId != null ? String(invoice.siteId) : '';
      const customerVal = invoice.customerId != null ? String(invoice.customerId) : '';
      const combinedNotes = [invoice.notes, invoice.termsOfSale].filter(Boolean).join('\n');
      const { parsed, remainingNotes } = parseNotesForFields(combinedNotes);
      const hasParsed = Object.keys(parsed).length > 0;
      const explicitTerms = invoice.termsOfSale ?? invoice.terms_of_sale ?? '';
      let specialNotesVal = hasParsed ? remainingNotes : (invoice.notes ? String(invoice.notes) : prev.specialNotes);
      let termsOfSaleVal = hasParsed ? '' : (invoice.termsOfSale ? String(invoice.termsOfSale) : prev.termsOfSale);
      if (hasParsed && remainingNotes && remainingNotes.trim()) {
        const lines = remainingNotes.split(/\n/).map((l) => l.trim()).filter(Boolean);
        if (lines.length >= 2) {
          specialNotesVal = lines[0];
          termsOfSaleVal = lines.slice(1).join('\n');
        } else if (lines.length === 1 && !explicitTerms) {
          specialNotesVal = lines[0];
          termsOfSaleVal = '';
        }
      }
      setFormData((prev) => ({
        ...prev,
        siteSelection: siteVal,
        clientSelection: customerVal,
        date: invoice.date || prev.date,
        dateOfExpiry: invoice.dueDate || prev.dateOfExpiry,
        paymentTerms: invoice.paymentTerms && invoice.paymentTerms !== 'N/A' ? String(invoice.paymentTerms) : prev.paymentTerms,
        paymentMode: parsed.paymentMode ?? (invoice.paymentMode && invoice.paymentMode !== 'N/A' ? String(invoice.paymentMode) : prev.paymentMode),
        billingAddress: siteVal,
        shippingAddress: siteVal,
        specialNotes: specialNotesVal ?? prev.specialNotes,
        termsOfSale: termsOfSaleVal ?? prev.termsOfSale,
        poNumber: parsed.poNumber ?? prev.poNumber,
        poDate: parsed.poDate ?? prev.poDate,
        modeOfTransaction: parsed.modeOfTransaction ?? prev.modeOfTransaction,
        transactionId: parsed.transactionId ?? prev.transactionId,
        amountPaid: parsed.amountPaid ?? prev.amountPaid,
        invoiceStatus: (() => {
          const s = invoice.status && typeof invoice.status === 'string' ? invoice.status.toLowerCase() : undefined;
          return (s === 'sent' || s === 'paid' ? s : undefined) ?? prev.invoiceStatus;
        })(),
        // POC for delivery: delivery contact first (shipping_details.contact), then flat poc_* / parsed notes; client only as fallback
        pocName: parsed.pocName ?? invoice.shipping_details?.contact?.name ?? invoice.pocName ?? invoice.poc_name ?? invoice.poc_client?.name ?? invoice.billing_contact ?? invoice.contact_name ?? prev.pocName,
        pocPhone: parsed.pocPhone ?? invoice.shipping_details?.contact?.phone ?? invoice.shipping_details?.contact?.mobile ?? invoice.shipping_details?.contact?.mobile_no ?? invoice.pocPhone ?? invoice.poc_phone ?? invoice.poc_client?.phone ?? invoice.poc_client?.mobile ?? invoice.billing_phone ?? invoice.contact_phone ?? prev.pocPhone,
        pocRole: parsed.pocRole ?? invoice.shipping_details?.contact?.role ?? invoice.pocRole ?? invoice.poc_role ?? invoice.poc_client?.role ?? prev.pocRole,
        pocEmail: parsed.pocEmail ?? invoice.shipping_details?.contact?.email ?? invoice.pocEmail ?? invoice.poc_email ?? invoice.poc_client?.email ?? invoice.billing_email ?? invoice.contact_email ?? prev.pocEmail,
        // Shipping details: from shipping_details object first, then flat fields (coerce to string for form)
        shippingPackages: String(invoice.shipping_details?.packages ?? invoice.shipping_details?.no_of_packages ?? invoice.shippingPackages ?? invoice.shipping_packages ?? prev.shippingPackages ?? ''),
        grossQuantity: String(invoice.shipping_details?.gross_quantity ?? invoice.shipping_details?.est_gross_quantity ?? invoice.grossQuantity ?? invoice.gross_quantity ?? prev.grossQuantity ?? ''),
        netQuantity: String(invoice.shipping_details?.net_quantity ?? invoice.shipping_details?.est_net_quantity ?? invoice.netQuantity ?? invoice.net_quantity ?? prev.netQuantity ?? ''),
        transportationMode: String(invoice.shipping_details?.mode_of_transportation ?? invoice.shipping_details?.transportation_mode ?? invoice.transportationMode ?? invoice.transportation_mode ?? prev.transportationMode ?? ''),
        transportationTerms: String(invoice.shipping_details?.transportation_terms ?? invoice.transportationTerms ?? invoice.transportation_terms ?? prev.transportationTerms ?? ''),
        // Est. Ship Date: from shipping_details, then flat fields; normalize to YYYY-MM-DD
        estimatedShipDate: (() => {
          const raw = invoice.shipping_details?.estimated_ship_date ?? invoice.estimatedShipDate ?? invoice.estimated_ship_date ?? invoice.ship_date ?? invoice.estimated_delivery_date ?? invoice.delivery_date ?? prev.estimatedShipDate ?? '';
          if (!raw) return prev.estimatedShipDate ?? '';
          if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
          const d = new Date(raw);
          return Number.isNaN(d.getTime()) ? (prev.estimatedShipDate ?? '') : d.toISOString().split('T')[0];
        })(),
      }));
      // Line items: amount = quantity × rate (never use line_total, which may include tax)
      const items = Array.isArray(invoice.lineItems) && invoice.lineItems.length > 0
        ? invoice.lineItems.map((item) => {
            const q = numberOrZero(item.quantity);
            const r = numberOrZero(item.rate);
            const amt = (q && r) ? q * r : (item.line_subtotal != null ? Number(item.line_subtotal) : (item.amount != null ? Number(item.amount) : 0));
            return {
              name: item.name ?? '',
              hsnSacCode: item.hsnSacCode ?? item.hsn_code ?? item.hsnCode ?? item.sac_code ?? item.sacCode ?? '',
              quantity: String(item.quantity ?? ''),
              units: item.units ?? item.unit ?? 'unit',
              rate: String(item.rate ?? ''),
              amount: amt !== 0 ? String(Number(amt).toFixed(2)) : '',
            };
          })
        : [createEmptyLineItem()];
      setLineItems(items);
      // Discount and charges: type, value, shippingHandling, insurance (from invoice or parsed notes)
      // API often returns discount as amount (currency) subtracted from total; form "Enter %" needs percentage so grand total matches
      const discountType = invoice.discountType ?? invoice.discount_type ?? 'percentage';
      const subtotalForDiscount = Number(invoice.subtotal) || 0;
      const discountAmountFromApi = invoice.discount != null && invoice.discount !== undefined ? Number(invoice.discount) : null;
      const hasExplicitPercentage = invoice.discount_value != null || invoice.discount_percentage != null || invoice.discount_percent != null || invoice.discount_pct != null;
      let discountVal = invoice.discountValue ?? invoice.discount_value ?? invoice.discount_percentage ?? invoice.discount_percent ?? invoice.discount_pct ?? '';
      if (discountVal !== '' && discountVal != null) {
        discountVal = String(discountVal);
      } else if (discountType === 'amount' && discountAmountFromApi != null) {
        discountVal = String(discountAmountFromApi);
      } else if (discountAmountFromApi != null && subtotalForDiscount > 0 && !hasExplicitPercentage) {
        // API sent discount as amount; derive percentage so form recalc matches backend grand total
        const pct = (discountAmountFromApi / subtotalForDiscount) * 100;
        discountVal = Number.isFinite(pct) ? String(Number(pct).toFixed(2)) : '';
      } else {
        discountVal = (parsed.discount != null && parsed.discount !== '') ? String(parsed.discount) : '';
      }
      setDiscount((prev) => ({
        ...prev,
        type: discountType === 'amount' ? 'amount' : 'percentage',
        value: discountVal,
        shippingHandling: invoice.shippingHandling != null ? String(invoice.shippingHandling) : prev.shippingHandling,
        insurance: parsed.insurance ?? (invoice.insurance != null ? String(invoice.insurance) : prev.insurance),
      }));
      // Tax details: one row from totalTax; derive % from the base tax was applied to (subtotal after discount)
      const subtotal = Number(invoice.subtotal) || 0;
      const discountAmount = Number(invoice.discount ?? 0) || 0;
      const subtotalAfterDiscount = Math.max(subtotal - discountAmount, 0);
      const totalTax = Number(invoice.totalTax ?? invoice.tax_total ?? invoice.tax_amount) || 0;
      const taxBase = subtotalAfterDiscount > 0 ? subtotalAfterDiscount : subtotal;
      if (totalTax > 0 && taxBase > 0) {
        const taxPct = ((totalTax / taxBase) * 100).toFixed(2);
        setTaxRows([
          {
            id: uniqueId(),
            label: 'Tax',
            applyOn: 'subtotalAfterDiscount',
            percentage: String(taxPct),
          },
        ]);
      }
    };

    if (fromFdoc && stateInvoice.id) {
      invoiceApi.getInvoice(stateInvoice.id).then((result) => {
        if (result.success && result.invoice && !appliedPrefillRef.current) {
          const inv = result.invoice;
          const lines = result.lines || [];
          // Pass through raw API objects so applyPrefill can read shipping_details.contact and poc_client
          const fullInvoice = {
            ...stateInvoice,
            customerId: inv.customer_id ?? stateInvoice.customerId,
            siteId: inv.site_id ?? stateInvoice.siteId,
            date: inv.invoice_date ?? stateInvoice.date,
            dueDate: inv.due_date ?? stateInvoice.dueDate,
            paymentTerms: inv.payment_terms ?? stateInvoice.paymentTerms,
            paymentMode: inv.payment_mode ?? stateInvoice.paymentMode,
            notes: inv.notes ?? stateInvoice.notes,
            subtotal: inv.subtotal ?? stateInvoice.subtotal,
            totalTax: inv.tax_total ?? inv.tax_amount ?? stateInvoice.totalTax,
            grandTotal: inv.grand_total ?? stateInvoice.grandTotal,
            shippingHandling: inv.shipping_total ?? inv.shipping_handling ?? stateInvoice.shippingHandling,
            insurance: inv.insurance ?? stateInvoice.insurance,
            discount: inv.discount ?? stateInvoice.discount,
            shipping_details: inv.shipping_details ?? stateInvoice.shipping_details,
            poc_client: inv.poc_client ?? stateInvoice.poc_client,
            // POC for delivery: from shipping_details.contact (delivery) first, then poc_client / flat
            pocName: inv.shipping_details?.contact?.name ?? inv.poc_client?.name ?? inv.poc_name ?? inv.pocName ?? inv.billing_contact ?? inv.contact_name ?? stateInvoice.pocName,
            pocPhone: inv.shipping_details?.contact?.phone ?? inv.shipping_details?.contact?.mobile ?? inv.shipping_details?.contact?.mobile_no ?? inv.poc_client?.phone ?? inv.poc_client?.mobile ?? inv.poc_phone ?? inv.pocPhone ?? inv.billing_phone ?? inv.contact_phone ?? stateInvoice.pocPhone,
            pocRole: inv.shipping_details?.contact?.role ?? inv.poc_client?.role ?? inv.poc_role ?? inv.pocRole ?? stateInvoice.pocRole,
            pocEmail: inv.shipping_details?.contact?.email ?? inv.poc_client?.email ?? inv.poc_email ?? inv.pocEmail ?? inv.billing_email ?? inv.contact_email ?? stateInvoice.pocEmail,
            // Shipping details: from shipping_details object first, then flat (coerce to string for form)
            shippingPackages: String(inv.shipping_details?.packages ?? inv.shipping_details?.no_of_packages ?? inv.shipping_packages ?? inv.shippingPackages ?? stateInvoice.shippingPackages ?? ''),
            grossQuantity: String(inv.shipping_details?.gross_quantity ?? inv.shipping_details?.est_gross_quantity ?? inv.gross_quantity ?? inv.grossQuantity ?? stateInvoice.grossQuantity ?? ''),
            netQuantity: String(inv.shipping_details?.net_quantity ?? inv.shipping_details?.est_net_quantity ?? inv.net_quantity ?? inv.netQuantity ?? stateInvoice.netQuantity ?? ''),
            transportationMode: String(inv.shipping_details?.mode_of_transportation ?? inv.shipping_details?.transportation_mode ?? inv.transportation_mode ?? inv.transportationMode ?? stateInvoice.transportationMode ?? ''),
            transportationTerms: String(inv.shipping_details?.transportation_terms ?? inv.transportation_terms ?? inv.transportationTerms ?? stateInvoice.transportationTerms ?? ''),
            discountType: inv.discount_type ?? inv.discountType ?? stateInvoice.discountType,
            // Pass percentage for form; if API only has discount (amount), derive % so grand total matches
            discountValue: (inv.discount_value != null ? String(inv.discount_value) : null) ?? (inv.discount_percentage != null ? String(inv.discount_percentage) : null) ?? (inv.discount_percent != null ? String(inv.discount_percent) : null) ?? (inv.discount_pct != null ? String(inv.discount_pct) : null) ?? (inv.discount_type === 'amount' && inv.discount != null ? String(inv.discount) : null) ?? (inv.discount != null && inv.discount !== undefined && inv.subtotal > 0 ? String((Number(inv.discount) / Number(inv.subtotal) * 100).toFixed(2)) : null) ?? stateInvoice.discountValue,
            // Est. Ship Date: from shipping_details or flat; normalize to YYYY-MM-DD for form
            estimatedShipDate: (() => {
              const raw = inv.shipping_details?.estimated_ship_date ?? inv.estimated_ship_date ?? inv.estimatedShipDate ?? inv.ship_date ?? inv.estimated_delivery_date ?? inv.delivery_date ?? stateInvoice.estimatedShipDate ?? '';
              if (!raw) return stateInvoice.estimatedShipDate ?? '';
              if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
              const d = new Date(raw);
              return Number.isNaN(d.getTime()) ? (stateInvoice.estimatedShipDate ?? '') : d.toISOString().split('T')[0];
            })(),
            lineItems: lines.map((line) => {
              const q = Number(line.quantity ?? 0);
              const rate = Number(line.unit_price ?? 0);
              const amount = line.line_subtotal != null ? Number(line.line_subtotal) : (q && rate ? q * rate : Number(line.line_total ?? 0));
              return {
                name: line.description || '',
                hsnSacCode: line.hsn_code || line.hsn || line.sac_code || '',
                quantity: line.quantity ?? 0,
                units: line.unit || '',
                rate: line.unit_price ?? 0,
                amount,
              };
            }),
          };
          applyPrefill(fullInvoice);
        } else if (!appliedPrefillRef.current) {
          applyPrefill(stateInvoice);
        }
      }).catch(() => {
        if (!appliedPrefillRef.current) {
          applyPrefill(stateInvoice);
        }
      });
    } else {
      applyPrefill(stateInvoice);
    }
  }, [location.state?.invoice, location.state?.fromFdoc, isSitesLoading, isCustomersLoading]);

  const siteOptions = useMemo(
    () =>
      sites
        .map((site) => ({
          value: resolveSiteValue(site),
          label: resolveSiteLabel(site),
        }))
        .filter((option) => option.value),
    [sites]
  );

  const findSiteLabel = (value) =>
    siteOptions.find((option) => option.value === value)?.label || '';

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.clientSelection) {
      setSubmitFeedback({ success: '', error: 'Please select a client/customer' });
      return;
    }

    if (lineItems.length === 0 || lineItems.some(item => !item.name || !item.quantity || !item.rate)) {
      setSubmitFeedback({ success: '', error: 'Please add at least one valid line item with name, quantity, and rate' });
      return;
    }

    setPocErrors({ phone: '', email: '' });
    const pocPhone = (formData.pocPhone || '').trim();
    const pocEmail = (formData.pocEmail || '').trim();
    if (pocPhone && !isValidPhone(pocPhone)) {
      setPocErrors({ phone: 'Enter a valid mobile number (e.g. +91 98765 43210 or 10 digits)', email: '' });
      setSubmitFeedback({ success: '', error: 'Please enter a valid POC mobile number.' });
      return;
    }
    if (pocEmail && !isValidEmail(pocEmail)) {
      setPocErrors({ phone: '', email: 'Enter a valid email address' });
      setSubmitFeedback({ success: '', error: 'Please enter a valid POC email address.' });
      return;
    }

    setIsSubmitting(true);
    setSubmitFeedback({ success: '', error: '' });

    try {
      // Find customer by ID (the dropdown value should be the customer_id)
      // Log all customers for debugging
      console.log('Invoice: Looking for customer:', formData.clientSelection);
      console.log('Invoice: Available customers:', customers.map(c => ({
        customer_id: c.customer_id,
        id: c.id,
        name: c.customer_name || c.name
      })));
      
      const selectedCustomer = customers.find(
        c => {
          const cId = c.customer_id || c.id;
          return cId === formData.clientSelection;
        }
      );

      if (!selectedCustomer) {
        console.error('Customer lookup failed:', {
          clientSelection: formData.clientSelection,
          availableCustomers: customers.map(c => ({
            customer_id: c.customer_id,
            id: c.id,
            name: c.customer_name || c.name
          }))
        });
        throw new Error(`Selected customer not found in the list. Please select a different customer. Customer ID: ${formData.clientSelection}`);
      }

      // Prefer customer_id over id
      const customerId = selectedCustomer.customer_id || selectedCustomer.id;
      
      // Log the actual customer object to see what we have
      console.log('Invoice: Selected customer object:', {
        customer_id: selectedCustomer.customer_id,
        id: selectedCustomer.id,
        customer_name: selectedCustomer.customer_name,
        name: selectedCustomer.name,
        full: selectedCustomer
      });
      
      if (!customerId) {
        throw new Error('Customer ID is missing. Please select a valid customer.');
      }

      console.log('Selected customer:', {
        customerId,
        customerName: selectedCustomer.customer_name || selectedCustomer.name,
        fullCustomer: selectedCustomer
      });

      // Skip customer verification due to CORS issues with the customer API
      // The invoice API will validate the customer and return a proper error if it doesn't exist
      // This is more reliable than trying to verify beforehand
      console.log('Skipping customer verification (invoice API will validate):', {
        customerId,
        customerName: selectedCustomer.customer_name || selectedCustomer.name
      });

      // Map line items to API format
      // Note: The API supports tax_rate per line item, but the form has tax rows that apply to different bases
      // For now, we'll distribute the tax across line items proportionally
      const totalLineSubtotal = summary.subtotal;
      const mappedLines = lineItems.map((item, index) => {
        const quantity = parseFloat(item.quantity) || 0;
        const unitPrice = parseFloat(item.rate) || 0;
        const lineSubtotal = quantity * unitPrice;
        
        // Calculate tax rate for this line item based on proportional tax
        // If tax is applied to subtotal after discount, distribute proportionally
        let taxRate = 0;
        if (totalLineSubtotal > 0 && summary.totalTax > 0) {
          // Find taxes that apply to subtotalAfterDiscount
          const applicableTaxes = summary.taxes.filter(t => 
            t.applyOn === 'subtotalAfterDiscount' || t.applyOn === 'subtotal'
          );
          const applicableTaxAmount = applicableTaxes.reduce((sum, t) => sum + t.amount, 0);
          if (applicableTaxAmount > 0 && summary.subtotalAfterDiscount > 0) {
            // Calculate what percentage of the tax this line item represents
            const lineProportion = lineSubtotal / totalLineSubtotal;
            const lineTaxAmount = applicableTaxAmount * lineProportion;
            // Calculate tax rate percentage
            taxRate = lineTaxAmount > 0 && lineSubtotal > 0 
              ? (lineTaxAmount / lineSubtotal) * 100 
              : 0;
          }
        }

        return {
          line_id: String(index + 1).padStart(3, '0'),
          item_id: `item-${index + 1}`, // TODO: Replace with actual item IDs from inventory if available
          description: item.name || '',
          unit: item.units || 'unit',
          quantity: quantity,
          unit_price: unitPrice,
          ...(item.hsnSacCode?.trim() && {
            hsn_code: item.hsnSacCode.trim(),
            sac_code: item.hsnSacCode.trim(),
          }),
          // tax_rate is optional - API allows zero, but we only include if > 0
          tax_rate: taxRate > 0 ? parseFloat(taxRate.toFixed(2)) : undefined,
          notes: '', // Line item notes (optional)
        };
      });

      // Calculate due date (default to 30 days from invoice date)
      const invoiceDate = formData.date || getTodayDate();
      const dueDateObj = new Date(invoiceDate);
      dueDateObj.setDate(dueDateObj.getDate() + 30);
      const dueDate = formData.dateOfExpiry || dueDateObj.toISOString().split('T')[0];

      // Get site details for addresses
      const billingSite = sites.find(s => resolveSiteValue(s) === formData.billingAddress);
      const shippingSite = formData.useBillingForShipping 
        ? billingSite 
        : sites.find(s => resolveSiteValue(s) === formData.shippingAddress);

      // Map addresses (simplified - you may need to get full address from site)
      const billingAddress = billingSite ? {
        street: billingSite.site_name || billingSite.name || '',
        city: billingSite.city || '',
        state: billingSite.state || '',
        pincode: billingSite.pincode || billingSite.postal_code || '',
        country: billingSite.country || 'India',
      } : undefined;

      const shippingAddress = shippingSite ? {
        street: shippingSite.site_name || shippingSite.name || '',
        city: shippingSite.city || '',
        state: shippingSite.state || '',
        pincode: shippingSite.pincode || shippingSite.postal_code || '',
        country: shippingSite.country || 'India',
      } : undefined;

      // Build API payload (site_id required for invoice to appear in Fdoc / backend)
      const siteId = formData.siteSelection ? String(formData.siteSelection).trim() : undefined;
      const isFromFdoc = location.state?.fromFdoc === true || !!location.state?.invoice;
      const amountPaid = numberOrZero(formData.amountPaid);
      const paymentStatus = isFromFdoc
        ? (amountPaid >= summary.grandTotal ? 'paid' : amountPaid > 0 ? 'partial' : 'unpaid')
        : 'unpaid';
      // Notes: special notes + structured lines only; do not include terms of sale here (sent as terms_of_sale)
      const notesLines = [
        formData.specialNotes,
        formData.poNumber ? `PO Number: ${formData.poNumber}` : '',
        formData.poDate ? `PO Date: ${formData.poDate}` : '',
        formData.paymentMode ? `Payment Mode: ${formData.paymentMode}` : '',
        discount.insurance ? `Insurance: ₹${discount.insurance}` : '',
        ...(isFromFdoc ? [
          formData.modeOfTransaction ? `Mode of transaction: ${formData.modeOfTransaction}` : '',
          formData.transactionId ? `Transaction ID: ${formData.transactionId}` : '',
          formData.amountPaid ? `Amount paid: ₹${formData.amountPaid}` : '',
        ] : []),
      ].filter(Boolean);
      // POC for delivery: API expects poc_* (snake_case) and/or shipping_details.contact
      const pocContact = (formData.pocName || formData.pocPhone || formData.pocEmail || formData.pocRole)
        ? {
            name: (formData.pocName || '').trim() || undefined,
            phone: (formData.pocPhone || '').trim() || undefined,
            email: (formData.pocEmail || '').trim() || undefined,
            role: (formData.pocRole || '').trim() || undefined,
          }
        : undefined;
      const trimmedPoc = (obj) => obj && Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null && v !== ''));

      // Shipping details: API expects shipping_details (snake_case)
      const shippingDetailsPayload = (() => {
        const packages = (formData.shippingPackages || '').trim();
        const grossQty = (formData.grossQuantity || '').trim();
        const netQty = (formData.netQuantity || '').trim();
        const transportMode = (formData.transportationMode || '').trim();
        const transportTerms = (formData.transportationTerms || '').trim();
        const contact = trimmedPoc(pocContact);
        if (!contact && !packages && !grossQty && !netQty && !transportMode && !transportTerms) return undefined;
        return {
          ...(contact && { contact }),
          ...(packages && { packages: isNaN(Number(packages)) ? packages : Number(packages) }),
          ...(grossQty && { gross_quantity: grossQty }),
          ...(netQty && { net_quantity: netQty }),
          ...(transportMode && { mode_of_transportation: transportMode }),
          ...(transportTerms && { transportation_terms: transportTerms }),
        };
      })();

      // Discount amount in currency (backend subtracts from total); send 0 if none
      const discountAmount = summary.computedDiscount != null ? Number(summary.computedDiscount) : 0;
      // Est. Ship Date: API accepts estimated_ship_date, est_ship_date, or estimatedShipDate (ISO or YYYY-MM-DD)
      const estimatedShipDateVal = (formData.estimatedShipDate || '').trim();
      const estimatedShipDatePayload = estimatedShipDateVal
        ? (/\d{4}-\d{2}-\d{2}/.test(estimatedShipDateVal) ? estimatedShipDateVal : new Date(estimatedShipDateVal).toISOString().split('T')[0])
        : undefined;

      const apiPayload = {
        customer_id: customerId,
        ...(siteId && { site_id: siteId }),
        invoice_date: invoiceDate,
        due_date: dueDate,
        status: isFromFdoc ? 'paid' : 'sent',
        payment_status: paymentStatus,
        currency: 'INR',
        lines: mappedLines,
        shipping_total: parseFloat(discount.shippingHandling) || 0,
        discount: discountAmount,
        ...(estimatedShipDatePayload && { estimated_ship_date: estimatedShipDatePayload }),
        payment_terms: formData.paymentTerms || undefined,
        ...(formData.termsOfSale?.trim() && { terms_of_sale: formData.termsOfSale.trim() }),
        ...(isFromFdoc && formData.modeOfTransaction && { mode_of_transaction: formData.modeOfTransaction }),
        ...(isFromFdoc && formData.transactionId && { transaction_id: formData.transactionId.trim() }),
        ...(isFromFdoc && amountPaid > 0 && { amount_paid: amountPaid }),
        notes: notesLines.join('\n') || undefined,
        billing_address: billingAddress,
        shipping_address: shippingAddress,
        // POC: flat poc_* (snake_case) so backend stores delivery POC and GET returns for prefill
        ...(formData.pocName?.trim() && { poc_name: formData.pocName.trim() }),
        ...(formData.pocPhone?.trim() && { poc_phone: formData.pocPhone.trim() }),
        ...(formData.pocRole?.trim() && { poc_role: formData.pocRole.trim() }),
        ...(formData.pocEmail?.trim() && { poc_email: formData.pocEmail.trim() }),
        // Shipping details (include estimated_ship_date here too if backend stores it under shipping_details)
        ...(shippingDetailsPayload && Object.keys(shippingDetailsPayload).length > 0 && {
          shipping_details: {
            ...shippingDetailsPayload,
            ...(estimatedShipDatePayload && { estimated_ship_date: estimatedShipDatePayload }),
          },
        }),
      };
      // If no other shipping details but we have est. ship date, send shipping_details with just estimated_ship_date
      if (!apiPayload.shipping_details && estimatedShipDatePayload) {
        apiPayload.shipping_details = { estimated_ship_date: estimatedShipDatePayload };
      }

      const existingInvoiceId = location.state?.invoice?.id;

      // From Fdoc with existing Sent invoice: update that invoice to Paid (no new card)
      if (isFromFdoc && existingInvoiceId) {
        const updatePayload = {
          status: 'paid',
          payment_status: paymentStatus,
          ...(formData.modeOfTransaction && { mode_of_transaction: formData.modeOfTransaction }),
          ...(formData.transactionId && { transaction_id: formData.transactionId.trim() }),
          ...(amountPaid > 0 && { amount_paid: amountPaid }),
          notes: notesLines.join('\n') || undefined,
        };
        const result = await invoiceApi.updateInvoice(existingInvoiceId, updatePayload);
        if (result.success) {
          setSubmitFeedback({
            success: `Invoice updated successfully! Status changed to Paid.`,
            error: ''
          });

          // Navigate to invoice view screen with updated status
          if (formData.siteSelection) {
            const invoiceDate = result.invoice?.invoice_date || formData.date;
            let year, month;
            if (invoiceDate && invoiceDate.includes('-')) {
              [year, month] = invoiceDate.split('-');
            } else {
              const now = new Date();
              year = now.getFullYear();
              month = String(now.getMonth() + 1).padStart(2, '0');
            }
            const monthKey = `${year}-${month}`;

            const selectedSiteObj = sites.find(s =>
              resolveSiteValue(s) === formData.siteSelection
            );

            setTimeout(() => {
              navigate('/onboarding-create-invoice/view', {
                state: {
                  siteId: formData.siteSelection,
                  site: selectedSiteObj,
                  month: monthKey,
                  invoiceId: result.invoice?.invoice_id || existingInvoiceId,
                  refresh: true,
                }
              });
            }, 500);
          }
          return;
        }
        throw new Error(result.error || 'Failed to update invoice status');
      }

      // Normal Create Invoice: create new invoice
      console.log('Creating invoice with payload:', apiPayload);
      console.log('Customer details being sent:', {
        customer_id: customerId,
        customer_name: selectedCustomer.customer_name || selectedCustomer.name,
        customer_object: selectedCustomer
      });

      const result = await invoiceApi.createInvoice(apiPayload);

      if (result.success) {
        setSubmitFeedback({ 
          success: `Invoice created successfully! Invoice Number: ${result.invoice?.invoice_number || 'N/A'}`,
          error: '' 
        });
        
        // Also open the print overlay with the created invoice data
        const invoicePayload = {
          invoiceNumber: result.invoice?.invoice_number || `INV-${Date.now().toString().slice(-6)}`,
          generatedAt: new Date().toISOString(),
          formData: { ...formData },
          summaryPayment: { invoiceStatus: formData.invoiceStatus },
          lineItems: lineItems.map((item, index) => ({
            ...item,
            lineNumber: index + 1,
            amount: item.amount || (parseFloat(item.quantity) * parseFloat(item.rate)).toFixed(2),
          })),
          discount: { ...discount },
          summary: {
            ...summary,
            taxes: summary.taxes.map((row) => ({ ...row })),
          },
          taxRows: taxRows.map((row) => ({ ...row })),
          labels: {
            site: findSiteLabel(formData.siteSelection),
            billingAddress: findSiteLabel(formData.billingAddress),
            shippingAddress: findSiteLabel(
              formData.useBillingForShipping ? formData.billingAddress : formData.shippingAddress
            ),
          },
          apiInvoice: result.invoice,
          apiLines: result.lines,
        };
        
        // On PDF close: navigate to Fdoc with Invoice OO and selected site
        const invoiceDate = result.invoice?.invoice_date || formData.date;
        let year, month;
        if (invoiceDate && invoiceDate.includes('-')) {
          [year, month] = invoiceDate.split('-');
        } else {
          const now = new Date();
          year = now.getFullYear();
          month = String(now.getMonth() + 1).padStart(2, '0');
        }
        const monthKey = `${year}-${month}`;
        const selectedSiteObj = sites.find(s =>
          resolveSiteValue(s) === formData.siteSelection
        );

        openOverlay('invoicePrint', {
          invoice: invoicePayload,
          onClose: () => {
            navigate('/tax-fdoc', {
              state: {
                restoreFdoc: true,
                selectedFdocType: 'Invoice',
                selectedSite: selectedSiteObj ?? formData.siteSelection,
                selectedMonth: monthKey,
              }
            });
          }
        });
      } else {
        // Check if it's a customer not found error
        if (result.error && result.error.includes('Customer not found')) {
          throw new Error(
            `Customer not found (404). The selected customer "${selectedCustomer.customer_name || selectedCustomer.name}" (ID: ${customerId}) ` +
            `does not exist in the invoice system. This may be due to:\n` +
            `1. Tenant/data isolation - the customer exists in a different tenant context\n` +
            `2. The customer was deleted or doesn't exist in the invoice system\n` +
            `3. Data inconsistency between customer and invoice systems\n\n` +
            `Please try:\n` +
            `- Select a different customer from the list\n` +
            `- Create a new customer and try again\n` +
            `- Contact support if the issue persists`
          );
        }
        throw new Error(result.error || 'Failed to create invoice');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      setSubmitFeedback({ 
        success: '', 
        error: error.message || 'Failed to create invoice. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
      <form
        onSubmit={handleCreateInvoice}
        className="mx-auto flex max-w-7xl flex-col gap-8"
      >
        <header className="space-y-2">
          <p className="text-sm uppercase tracking-wide text-gray-500">
            Billing · Invoice
          </p>
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-900">Create Invoice</h1>
              <p className="text-gray-500">
                Capture billing, shipping and taxation details in a single flow.
              </p>
            </div>
            {/* <button
              type="submit"
              className="inline-flex items-center justify-center rounded-xl bg-green-600 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
            >
              Create Invoice
            </button> */}
          </div>
        </header>

        <section className="grid gap-2 xl:grid-cols-3">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">Invoice Raised For</p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Site Selection <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.siteSelection}
                  onChange={(e) => handleFormChange('siteSelection', e.target.value)}
                  className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  disabled={isSitesLoading}
                >
                  <option value="">
                    {isSitesLoading ? 'Loading sites...' : 'Select a site'}
                  </option>
                  {/* alphabetical order */}
                  {siteOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {sitesError && (
                  <p className="mt-1 text-xs text-red-600">{sitesError}</p>
                )}
                {!isSitesLoading && !siteOptions.length && !sitesError && (
                  <p className="mt-1 text-xs text-gray-500">
                    No sites available. Please create a site first.
                  </p>
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-500">Date (Invoice) <span className="text-red-500">*</span></label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleFormChange('date', e.target.value)}
                    max={getTodayDate()}
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">PO #</label>
                  <Input
                    value={formData.poNumber}
                    onChange={(e) => handleFormChange('poNumber', e.target.value)}
                    placeholder="Enter purchase order number"
                    className="mt-1 h-12 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">PO Date</label>
                  <input
                    type="date"
                    value={formData.poDate}
                    onChange={(e) => handleFormChange('poDate', e.target.value)}
                    max={toYYYYMMDD(formData.date)}
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Payment Mode
                  </label>
                  <select
                    value={formData.paymentMode}
                    onChange={(e) => handleFormChange('paymentMode', e.target.value)}
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  >
                    <option value="" disabled>
                      Select mode
                    </option>
                    <option value="bank-transfer">Bank Transfer</option>
                    <option value="upi">UPI</option>
                    <option value="cheque">Cheque</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-gray-500">
                    Payment Terms
                  </label>
                  <textarea
                    value={formData.paymentTerms}
                    onChange={(e) => handleFormChange('paymentTerms', e.target.value)}
                    placeholder="e.g., Net 30, milestone-wise terms, etc."
                    rows={2}
                    className="mt-1 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Est. Ship Date
                  </label>
                  <input
                    type="date"
                    value={formData.estimatedShipDate}
                    onChange={(e) =>
                      handleFormChange('estimatedShipDate', e.target.value)
                    }
                    min={toYYYYMMDD(formData.date)}
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Date of Expiry (Invoice) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.dateOfExpiry}
                    onChange={(e) => handleFormChange('dateOfExpiry', e.target.value)}
                    min={toYYYYMMDD(formData.date)}
                    className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  />
                </div>
              </div>
              
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">
              Client & Billing Address
            </p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Client Selection <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.clientSelection}
                  onChange={(e) => handleFormChange('clientSelection', e.target.value)}
                  className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  disabled={isCustomersLoading}
                >
                  <option value="" disabled hidden>Select a customer</option>
                  {customers.map((customer) => {
                    // Use customer_id as primary, fallback to id only if customer_id doesn't exist
                    const customerId = customer.customer_id || customer.id;
                    if (!customerId) {
                      console.warn('Customer missing ID:', customer);
                      return null;
                    }
                    return (
                      <option key={customerId} value={customerId}>
                        {customer.customer_name || customer.name || 'Unknown Customer'}
                      </option>
                    );
                  })}
                </select>
                {customersError && (
                  <p className="mt-1 text-xs text-red-600">{customersError}</p>
                )}
                {!isCustomersLoading && !customers.length && !customersError && (
                  <p className="mt-1 text-xs text-gray-500">
                    No customers available. Please create a customer first.
                  </p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Billing Address (Site) <span className="text-red-500">*</span>
                </label>
                <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-gray-600">
                  <input
                    type="checkbox"
                    checked={formData.useBillingForShipping}
                    onChange={(e) =>
                      handleFormChange('useBillingForShipping', e.target.checked)
                    }
                    className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500 mt-2 mb-2"
                  />
                  Use the same address for Shipping Address
                </label>
                <select
                  value={formData.billingAddress}
                  onChange={(e) => handleFormChange('billingAddress', e.target.value)}
                  className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                  disabled={isSitesLoading}
                >
                  <option value="">
                    {isSitesLoading ? 'Loading sites...' : 'Select a site'}
                  </option>
                  {/* alphabetical order */}
                  {siteOptions.map((option) => (
                    <option key={`billing-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {sitesError && (
                  <p className="mt-1 text-xs text-red-600">{sitesError}</p>
                )}
                {!isSitesLoading && !siteOptions.length && !sitesError && (
                  <p className="mt-1 text-xs text-gray-500">
                    No sites available. Please create a site first.
                  </p>
                )}
              </div>
              <div className="space-y-4">

              <div>
                <label className="text-xs font-semibold text-gray-500">
                  Shipping Address (Site)
                </label>
                <select
                  value={formData.shippingAddress}
                  onChange={(e) => handleFormChange('shippingAddress', e.target.value)}
                  className="mt-1 h-12 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 disabled:cursor-not-allowed disabled:opacity-70"
                  disabled={isSitesLoading || formData.useBillingForShipping}
                >
                  <option value="">
                    {isSitesLoading ? 'Loading sites...' : 'Select a site'}
                  </option>
                  {/* alphabetical order */}
                  {siteOptions.map((option) => (
                    <option key={`shipping-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {formData.useBillingForShipping && (
                  <p className="mt-1 text-xs text-gray-500">
                    Shipping site matches billing site while the checkbox is enabled.
                  </p>
                )}
                {!formData.useBillingForShipping && !isSitesLoading && !siteOptions.length && !sitesError && (
                  <p className="mt-1 text-xs text-gray-500">
                    No sites available. Please create a site first.
                  </p>
                )}
                {sitesError && (
                  <p className="mt-1 text-xs text-red-600">{sitesError}</p>
                )}
              </div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">Shipping Details</p>
            <div className="mt-4 space-y-4">
              
              <div className="space-y-4">
              <div>
              <label className="text-xs font-semibold text-gray-500">
                    Mode of Transportation
                  </label>
                  <Input
                    value={formData.transportationMode}
                    onChange={(e) =>
                      handleFormChange('transportationMode', e.target.value)
                    }
                    placeholder="Road, Air, Rail..."
                    className="mt-1 h-12 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Transportation Terms
                  </label>
                  <Input
                    value={formData.transportationTerms}
                    onChange={(e) =>
                      handleFormChange('transportationTerms', e.target.value)
                    }
                    placeholder="FOB, CIF..."
                    className="mt-1 h-12 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    No. of Items / Packages
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.shippingPackages}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v !== '' && v !== '-' && parseFloat(v) < 0) {
                        handleFormChange('shippingPackages', '0');
                      } else {
                        handleFormChange('shippingPackages', v);
                      }
                    }}
                    placeholder="0"
                    className="mt-1 h-12 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Est. Gross Quantity
                  </label>
                  <Input
                    value={formData.grossQuantity}
                    onChange={(e) => handleFormChange('grossQuantity', e.target.value)}
                    placeholder="In Kg / Units"
                    className="mt-1 h-12 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Est. Net Quantity
                  </label>
                  <Input
                    value={formData.netQuantity}
                    onChange={(e) => handleFormChange('netQuantity', e.target.value)}
                    placeholder="In Kg / Units"
                    className="mt-1 h-12 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">POC for Delivery</p>
            <div className="mt-4 space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500">Name</label>
                <Input
                  value={formData.pocName}
                  onChange={(e) => handleFormChange('pocName', e.target.value)}
                  placeholder="Enter name"
                  className="mt-1 h-12 rounded-2xl border border-emerald-200 bg-white px-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Role</label>
                <Input
                  value={formData.pocRole}
                  onChange={(e) => handleFormChange('pocRole', e.target.value)}
                  placeholder="Enter role"
                  className="mt-1 h-12 rounded-2xl border border-emerald-200 bg-white px-4 text-sm focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-100"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Mobile No.</label>
                <Input
                  value={formData.pocPhone}
                  onChange={(e) => {
                    handleFormChange('pocPhone', e.target.value);
                    if (pocErrors.phone) setPocErrors((prev) => ({ ...prev, phone: '' }));
                  }}
                  placeholder="+91 98765 43210"
                  className={`mt-1 h-12 rounded-2xl border px-4 text-sm focus:outline-none focus:ring-2 ${
                    pocErrors.phone
                      ? 'border-red-300 bg-red-50/50 focus:border-red-500 focus:ring-red-500/40'
                      : 'border-emerald-200 bg-white focus:border-emerald-400 focus:ring-emerald-100'
                  }`}
                />
                {pocErrors.phone && (
                  <p className="mt-1 text-xs text-red-600">{pocErrors.phone}</p>
                )}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500">Email</label>
                <Input
                  value={formData.pocEmail}
                  onChange={(e) => {
                    handleFormChange('pocEmail', e.target.value);
                    if (pocErrors.email) setPocErrors((prev) => ({ ...prev, email: '' }));
                  }}
                  placeholder="name@company.com"
                  className={`mt-1 h-12 rounded-2xl border px-4 text-sm focus:outline-none focus:ring-2 ${
                    pocErrors.email
                      ? 'border-red-300 bg-red-50/50 focus:border-red-500 focus:ring-red-500/40'
                      : 'border-emerald-200 bg-white focus:border-emerald-400 focus:ring-emerald-100'
                  }`}
                />
                {pocErrors.email && (
                  <p className="mt-1 text-xs text-red-600">{pocErrors.email}</p>
                )}
              </div>
            </div>
          </div>
          <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">
              Special Notes & Terms of Sale
            </p>
            <div className="mt-4 space-y-4">
              <textarea
                value={formData.specialNotes}
                onChange={(e) => handleFormChange('specialNotes', e.target.value)}
                placeholder="Special delivery notes, handling instructions..."
                className="h-28 w-full resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
              />
              <textarea
                value={formData.termsOfSale}
                onChange={(e) => handleFormChange('termsOfSale', e.target.value)}
                placeholder="Terms of sale"
                className="h-28 w-full resize-none rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
              />
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-900">Line Items</p>
              <p className="text-sm text-gray-500">
                Manage services, quantities, units, and rates. Amount is auto-calculated.
              </p>
            </div>
            <button
              type="button"
              onClick={handleAddLineItem}
              className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-500 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
            >
              <PlusCircle className="h-4 w-4" />
              Add Line Item
            </button>
          </div>

          <div className="mt-6">
            {!isMobileView ? (
              <div className="overflow-x-auto rounded-2xl border border-gray-100">
                <table className="min-w-full border-collapse text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold">S. No</th>
                      <th className="px-4 py-3 text-left font-semibold min-w-[200px]">
                        Name <span className="text-red-500">*</span>
                      </th>
                      <th className="px-4 py-3 text-left font-semibold min-w-[140px]">
                        SAC / HSN Code
                      </th>
                      <th className="px-4 py-3 text-left font-semibold min-w-[120px]">
                        Quantity <span className="text-red-500">*</span>
                      </th>
                      <th className="px-4 py-3 text-left font-semibold min-w-[140px]">
                        Units (Per item)
                      </th>
                      <th className="px-4 py-3 text-left font-semibold min-w-[140px]">
                        Rate <span className="text-red-500">*</span>
                      </th>
                      <th className="px-4 py-3 text-left font-semibold min-w-[140px]">
                        Total Amount
                      </th>
                      <th className="px-4 py-3 text-right font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, index) => (
                      <tr key={`line-item-${index}`} className="border-t">
                        <td className="px-4 py-4 text-gray-700">{index + 1}</td>
                        <td className="px-4 py-4">
                          <Input
                            value={item.name}
                            onChange={(e) =>
                              handleLineItemChange(index, 'name', e.target.value)
                            }
                            placeholder="Item or service name"
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            value={item.hsnSacCode}
                            onChange={(e) =>
                              handleLineItemChange(index, 'hsnSacCode', e.target.value)
                            }
                            placeholder="Code"
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) =>
                              handleLineItemChange(index, 'quantity', e.target.value)
                            }
                            placeholder="0.00"
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            value={item.units}
                            onChange={(e) =>
                              handleLineItemChange(index, 'units', e.target.value)
                            }
                            placeholder="Kg, Nos..."
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) =>
                              handleLineItemChange(index, 'rate', e.target.value)
                            }
                            placeholder="0.00"
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </td>
                        <td className="px-4 py-4">
                          <Input
                            type="number"
                            readOnly
                            value={item.amount}
                            placeholder="0.00"
                            className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600"
                          />
                        </td>
                        <td className="px-4 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => handleRemoveLineItem(index)}
                            disabled={lineItems.length === 1}
                            className="inline-flex items-center gap-2 rounded-full border border-red-100 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Trash2 className="h-4 w-4" />
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t bg-gray-50">
                      <td
                        colSpan={6}
                        className="px-4 py-3 text-right text-sm font-semibold text-gray-700"
                      >
                        Subtotal (Taxable Amount)
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                        {formatCurrency(summary.subtotal)}
                      </td>
                      <td></td>
                    </tr>
                    <tr className="border-t bg-gray-50">
                      <td
                        colSpan={6}
                        className="px-4 py-3 text-right text-sm font-semibold text-gray-700"
                      >
                        {gstFooterLabel}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">
                        {formatCurrency(summary.totalTax)}
                      </td>
                      <td></td>
                    </tr>
                    <tr className="border-t bg-gray-100">
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-sm font-bold uppercase text-gray-900">
                        Total
                      </td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                        {formatTotalQuantity(summary.totalQuantity)}
                      </td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3"></td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                        {formatCurrency(summary.grandTotal)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="space-y-4 rounded-2xl border border-gray-100 bg-gray-50 p-1">
                {lineItems.map((item, index) => (
                  <div
                    key={`line-item-card-${index}`}
                    className="rounded-2xl border border-white bg-white p-4 shadow-sm"
                  >
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-700">
                        Item #{index + 1}
                      </p>
                      <button
                        type="button"
                        onClick={() => handleRemoveLineItem(index)}
                        disabled={lineItems.length === 1}
                        className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                        <span className="text-red-500">Remove</span>
                      </button>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-500">
                          Name / Description <span className="text-red-500">*</span>
                        </label>
                        <Input
                          value={item.name}
                          onChange={(e) =>
                            handleLineItemChange(index, 'name', e.target.value)
                          }
                          placeholder="Item or service name"
                          className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-gray-500">
                          SAC / HSN Code
                        </label>
                        <Input
                          type="number"
                          min="0"
                          step="1"
                          value={item.hsnSacCode}
                          onChange={(e) =>
                            handleLineItemChange(index, 'hsnSacCode', e.target.value)
                          }
                          placeholder="Code"
                          className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                        />
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Quantity <span className="text-red-500">*</span>
                          </label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.quantity}
                            onChange={(e) =>
                              handleLineItemChange(index, 'quantity', e.target.value)
                            }
                            placeholder="0.00"
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Units
                          </label>
                          <Input
                            value={item.units}
                            onChange={(e) =>
                              handleLineItemChange(index, 'units', e.target.value)
                            }
                            placeholder="Kg, Nos..."
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Rate <span className="text-red-500">*</span>
                          </label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) =>
                              handleLineItemChange(index, 'rate', e.target.value)
                            }
                            placeholder="0.00"
                            className="h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                          />
                        </div>
                        <div>
                          <label className="mb-1 block text-xs font-semibold text-gray-500">
                            Amount
                          </label>
                          <Input
                            type="number"
                            readOnly
                            value={item.amount}
                            placeholder="0.00"
                            className="h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <div className="space-y-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm">
                  <div className="flex items-center justify-between font-semibold text-gray-700">
                    <span>Subtotal (Taxable Amount)</span>
                    <span className="text-gray-900">{formatCurrency(summary.subtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between font-semibold text-gray-700">
                    <span>{gstFooterLabel}</span>
                    <span className="text-gray-900">{formatCurrency(summary.totalTax)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-200 pt-2 font-bold text-gray-900">
                    <span className="uppercase">
                      Total
                      {summary.totalQuantity > 0 && (
                        <span className="ml-2 font-semibold normal-case text-gray-700">
                          (Qty: {formatTotalQuantity(summary.totalQuantity)})
                        </span>
                      )}
                    </span>
                    <span>{formatCurrency(summary.grandTotal)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div>
            <p className="text-sm font-semibold text-gray-900">HSN/SAC Tax Summary</p>
            <p className="text-sm text-gray-500">
              Tax breakdown grouped by SAC/HSN code from line items.
            </p>
          </div>

          <div className="mt-6 overflow-x-auto rounded-2xl border border-gray-100">
            <table className="min-w-full border-collapse text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold">
                    HSN/SAC
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-right font-semibold">
                    Taxable Value
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-right font-semibold min-w-[120px]">
                    Central Tax Rate
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-right font-semibold">
                    Central Tax Amount
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-right font-semibold min-w-[120px]">
                    State/UT Tax Rate
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-right font-semibold">
                    State/UT Tax Amount
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-right font-semibold min-w-[120px]">
                    Integrated Tax Rate
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-right font-semibold">
                    Integrated Tax Amount
                  </th>
                  <th className="border border-gray-200 px-4 py-3 text-right font-semibold">
                    Total Tax Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {hsnSacTaxSummary.rows.length > 0 ? (
                  hsnSacTaxSummary.rows.map((row) => (
                    <tr key={`hsn-sac-tax-${row.code}`} className="text-gray-800">
                      <td className="border border-gray-200 px-4 py-3 font-semibold">
                        {row.code}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        {formatTaxTableAmount(row.taxableValue)}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.centralRateInput}
                          onChange={(e) =>
                            handleHsnSacRateChange(row.code, 'centralRate', e.target.value)
                          }
                          placeholder="%"
                          className="h-9 w-full min-w-[72px] rounded-xl border-gray-200 bg-gray-50 px-3 text-right text-sm"
                        />
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        {formatTaxTableAmount(row.centralAmount)}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.stateUtRateInput}
                          onChange={(e) =>
                            handleHsnSacRateChange(row.code, 'stateUtRate', e.target.value)
                          }
                          placeholder="%"
                          className="h-9 w-full min-w-[72px] rounded-xl border-gray-200 bg-gray-50 px-3 text-right text-sm"
                        />
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        {formatTaxTableAmount(row.stateAmount)}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.igstRateInput}
                          onChange={(e) =>
                            handleHsnSacRateChange(row.code, 'igstRate', e.target.value)
                          }
                          placeholder="%"
                          className="h-9 w-full min-w-[72px] rounded-xl border-gray-200 bg-gray-50 px-3 text-right text-sm"
                        />
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right">
                        {formatTaxTableAmount(row.igstAmount)}
                      </td>
                      <td className="border border-gray-200 px-4 py-3 text-right font-semibold">
                        {formatTaxTableAmount(row.totalTax)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={9}
                      className="border border-gray-200 px-4 py-4 text-center text-gray-500"
                    >
                      Tax breakdown will appear once line items are added.
                    </td>
                  </tr>
                )}
              </tbody>
              {hsnSacTaxSummary.rows.length > 0 && (
                <tfoot>
                  <tr className="bg-gray-50 font-bold text-gray-900">
                    <td className="border border-gray-200 px-4 py-3">Total</td>
                    <td className="border border-gray-200 px-4 py-3 text-right">
                      {formatTaxTableAmount(hsnSacTaxSummary.totals.taxableValue)}
                    </td>
                    <td className="border border-gray-200 px-4 py-3 text-right"></td>
                    <td className="border border-gray-200 px-4 py-3 text-right">
                      {formatTaxTableAmount(hsnSacTaxSummary.totals.centralAmount)}
                    </td>
                    <td className="border border-gray-200 px-4 py-3 text-right"></td>
                    <td className="border border-gray-200 px-4 py-3 text-right">
                      {formatTaxTableAmount(hsnSacTaxSummary.totals.stateAmount)}
                    </td>
                    <td className="border border-gray-200 px-4 py-3 text-right"></td>
                    <td className="border border-gray-200 px-4 py-3 text-right">
                      {formatTaxTableAmount(hsnSacTaxSummary.totals.igstAmount)}
                    </td>
                    <td className="border border-gray-200 px-4 py-3 text-right">
                      {formatTaxTableAmount(hsnSacTaxSummary.totals.totalTax)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </section>

        <section className="flex flex-col gap-6 lg:flex-row lg:items-start">
          <div className="flex-1 space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-gray-900">Discount & Charges</p>
            <div className="mt-4 space-y-5">
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Discount Type
                  </label>
                  <div className="inline-flex rounded-full border border-gray-200 bg-gray-50 p-1">
                    {['percentage', 'amount'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => handleDiscountChange('type', type)}
                        className={`rounded-full px-4 py-1 text-xs font-semibold capitalize transition ${
                          discount.type === type
                            ? 'bg-green-600 text-white'
                            : 'text-gray-600'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    {discount.type === 'percentage' ? 'Enter %' : 'Enter Amount'}
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discount.value}
                    onChange={(e) => handleDiscountChange('value', e.target.value)}
                    placeholder="0"
                    className="mt-1 h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Shipping / Handling
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discount.shippingHandling}
                    onChange={(e) =>
                      handleDiscountChange('shippingHandling', e.target.value)
                    }
                    placeholder="0.00"
                    className="mt-1 h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500">
                    Insurance
                  </label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discount.insurance}
                    onChange={(e) => handleDiscountChange('insurance', e.target.value)}
                    placeholder="0.00"
                    className="mt-1 h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                  />
                </div>
              </div>
            </div>
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex-1 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-900">Tax Details</p>
                <p className="text-sm text-gray-500">
                  Select applicable tax, choose base value, enter percentage. Amount gets
                  auto-calculated.
                </p>
              </div>
              <button
                type="button"
                onClick={handleAddTaxRow}
                className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-400 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:border-gray-500 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
              >
                <PlusCircle className="h-4 w-4" />
                Add Tax
              </button>
            </div>

            <div className="mt-6 space-y-4">
              {summary.taxes.map((row, index) => (
                <div
                  key={row.id}
                  className="grid gap-4 rounded-2xl border border-gray-100 bg-gray-50 p-4 md:grid-cols-[1.5fr_1fr_1fr_1fr_auto]"
                >
                  <div>
                    <label className="text-xs font-semibold text-gray-500">
                      Tax Label
                    </label>
                    <select
                      value={row.label}
                      onChange={(e) => handleTaxRowChange(row.id, 'label', e.target.value)}
                      className="mt-1 h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    >
                      <option value="" disabled hidden></option>
                      <option value="CGST">CGST</option>
                      <option value="IGST">IGST</option>
                      <option value="SGST">SGST</option>
                      <option value="GST (CGST & IGST)">GST (CGST & IGST)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">
                      On Which Amount
                    </label>
                    <select
                      value={row.applyOn}
                      onChange={(e) => handleTaxRowChange(row.id, 'applyOn', e.target.value)}
                      className="mt-1 h-11 w-full rounded-2xl border border-gray-200 bg-white px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    >
                      <option value="subtotalAfterDiscount">Subtotal (After Discount)</option>
                      <option value="shippingHandling">Shipping / Handling</option>
                      <option value="insurance">Insurance</option>
                      <option value="netTotalBeforeTax">Net total (before tax)</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">
                      Enter Value %
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.percentage}
                      onChange={(e) =>
                        handleTaxRowChange(row.id, 'percentage', e.target.value)
                      }
                      placeholder="18"
                      className="mt-1 h-11 rounded-2xl border-gray-200 bg-white px-4 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">
                      Amount (auto)
                    </label>
                    <Input
                      readOnly
                      value={row.amount.toFixed(2)}
                      className="mt-1 h-11 rounded-2xl border border-gray-200 bg-gray-100 px-4 text-sm text-gray-600"
                    />
                  </div>
                  <div className="flex items-end justify-end">
                    <button
                      type="button"
                      onClick={() => handleRemoveTaxRow(row.id)}
                      disabled={index === 0 && summary.taxes.length === 1}
                      className="inline-flex items-center gap-2 rounded-full border border-red-100 px-3 py-2 text-xs font-semibold text-red-600 transition hover:border-red-300 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
            </div>
          </div>

          <div className="w-full lg:w-[320px]">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <p className="text-sm font-semibold text-gray-900">Summary</p>
              <dl className="mt-4 space-y-3 text-sm text-gray-600">
                <div className="flex items-center justify-between">
                  <dt>Subtotal</dt>
                  <dd>₹ {summary.subtotal.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt>Discount</dt>
                  <dd>- ₹ {summary.computedDiscount.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between font-semibold text-gray-900">
                  <dt>Subtotal (After Discount)</dt>
                  <dd>₹ {summary.subtotalAfterDiscount.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt>Shipping / Handling</dt>
                  <dd>₹ {summary.shippingHandling.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt>Insurance</dt>
                  <dd>₹ {summary.insurance.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between font-semibold text-gray-900">
                  <dt>Net Total (Before Tax)</dt>
                  <dd>₹ {summary.netTotalBeforeTax.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt>Total Tax Amount</dt>
                  <dd>₹ {summary.totalTax.toFixed(2)}</dd>
                </div>
                <div className="flex items-center justify-between text-base font-bold text-green-700">
                  <dt>Grand Total</dt>
                  <dd>₹ {summary.grandTotal.toFixed(2)}</dd>
                </div>
              </dl>
            </div>

            {(location.state?.fromFdoc || location.state?.invoice) && (
              <div className="mt-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-gray-900">Payment & transaction</p>
                <div className="mt-4 space-y-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500">Status</label>
                    <select
                      value={formData.invoiceStatus}
                      onChange={(e) => handleFormChange('invoiceStatus', e.target.value)}
                      className="mt-1 h-11 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    >
                      <option value="sent">Sent</option>
                      <option value="paid">Paid</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">Mode of transaction</label>
                    <select
                      value={formData.modeOfTransaction}
                      onChange={(e) => handleFormChange('modeOfTransaction', e.target.value)}
                      className="mt-1 h-11 w-full rounded-2xl border border-gray-200 bg-gray-50 px-4 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40"
                    >
                      <option value="">Select mode</option>
                      <option value="bank-transfer">Bank Transfer</option>
                      <option value="upi">UPI</option>
                      <option value="cheque">Cheque</option>
                      <option value="cash">Cash</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">Transaction ID</label>
                    <Input
                      value={formData.transactionId}
                      onChange={(e) => handleFormChange('transactionId', e.target.value)}
                      placeholder="Enter transaction/reference ID"
                      className="mt-1 h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500">Amount paid</label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.amountPaid}
                      onChange={(e) => handleFormChange('amountPaid', e.target.value)}
                      placeholder="0.00"
                      className="mt-1 h-11 rounded-2xl border-gray-200 bg-gray-50 px-4 text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold text-gray-900">
              Grand Total Payable
            </p>
            <p className="text-3xl font-bold text-gray-900">
              ₹ {summary.grandTotal.toFixed(2)}
            </p>
            {submitFeedback.success && (
              <p className="mt-2 text-sm text-green-600">{submitFeedback.success}</p>
            )}
            {submitFeedback.error && (
              <p className="mt-2 text-sm text-red-600">{submitFeedback.error}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center justify-center rounded-2xl bg-green-600 px-8 py-3 text-base font-semibold text-white shadow-lg transition hover:bg-green-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting
              ? (location.state?.fromFdoc || location.state?.invoice ? 'Submitting...' : 'Creating...')
              : (location.state?.fromFdoc || location.state?.invoice ? 'Submit' : 'Create Invoice')}
          </button>
        </div>
      </form>
  );
};

export default CreateInvoice;